import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, faUpload, faFileText, faSpinner, faRobot 
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import CRMExtractionResults from './CRMExtractionResults';
import DocumentNameSuggestionForm from '../DocumentNaming/DocumentNameSuggestionForm';
import { useFlashMessage } from '../FlashMessage/FlashMessageContext';
import { getErrorMessage } from '../../utils/errorMessages';
import FileDropZone from '../shared/FileDropZone';

// Returns true if the given existing profile value should be treated as "empty"
// for the purposes of the "Fill from Document" merge flow. Used to decide
// whether to pre-check an extracted field by default.
const isProfileFieldEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') {
        // address-style objects: { formatted_address: '...' } counts as empty if no formatted_address
        if ('formatted_address' in value) {
            return !value.formatted_address || String(value.formatted_address).trim() === '';
        }
        return Object.keys(value).length === 0;
    }
    return false;
};

// Maps an extracted-field key (from the AI schema) to the corresponding key on
// the CRMProfile object so we can look up the current value. Banking and
// commodities don't map cleanly (they're child collections) and always pre-check
// so the user sees them — the child tabs do their own per-row dedupe.
const EXTRACTED_TO_PROFILE_FIELD = {
    legal_entity_name: 'legal_entity_name',
    trading_short_name: 'trading_short_name',
    head_office_address: 'head_office_address_data',
    invoice_address: 'invoice_address_data',
    registration_number: 'tax_registration_numbers',
    company_website_url: 'company_website_url',
    incorporation_date: 'incorporation_date',
    partner_type_selections: 'partner_type_selections',
    industry_selections: 'industry_selections',
};

const CRMDocumentUploadModal = ({ 
    isOpen, 
    onClose, 
    onSuccess,
    onStartDocumentScanning,
    documentScanningState,
    // Optional: when provided (i.e. "Fill from Document" on an existing partner),
    // CRMExtractionResults shows the current value next to each extracted field
    // and pre-checks only those whose corresponding profile field is empty.
    // When omitted (i.e. new-profile flow), every extracted field is pre-checked
    // as before — zero behavior change.
    existingProfileData = null,
    // KYC fill mode: analyze the partner's KYC documents already on file
    // (portal + DocuWare) instead of a freshly uploaded file. Skips the
    // file-pick step and the AI-naming form (the source documents are already
    // stored and named). kycPartnerId is the CRMProfile id to analyze.
    kycFillMode = false,
    kycPartnerId = null,
}) => {
    const [file, setFile] = useState(null);
    const [uploadState, setUploadState] = useState('idle'); // 'idle', 'extracting', 'results', 'error'
    const [error, setError] = useState('');
    const [extractedData, setExtractedData] = useState(null);
    const [selectedFields, setSelectedFields] = useState({});
    const [analysisId, setAnalysisId] = useState(null);
    const [extractionStep, setExtractionStep] = useState(0);
    const [namingPayload, setNamingPayload] = useState(null);
    const [pageProgress, setPageProgress] = useState({ current: 0, total: 0 });
    const [extractionSteps] = useState([
        'Analyzing document format and structure...',
        'Converting document to processable format...',
        'Extracting text using advanced OCR and parsing...',
        'Applying AI analysis to identify profile information...',
        'Extracting company details and partner data...',
        'Validating and structuring extracted data...',
        'Finalizing profile information extraction...'
    ]);

    const { showError, showSuccess, showInfo } = useFlashMessage();
    
    // Compute the initial selectedFields map for extracted data. When the user
    // is filling an existing partner, only pre-check fields the partner doesn't
    // already have a value for; for child collections (banking, commodities),
    // always pre-check because the child tabs do their own per-row dedupe.
    const computeInitialSelectedFields = useCallback((data) => {
        const initialSelected = {};
        if (!data) return initialSelected;
        Object.keys(data).forEach((key) => {
            if (data[key] === null || data[key] === undefined) return;
            // KYC-fill metadata keys are context for this modal, not profile fields.
            if (key === 'kyc_fill_mode' || key === 'source_documents') return;
            if (!existingProfileData) {
                initialSelected[key] = true;
                return;
            }
            const profileKey = EXTRACTED_TO_PROFILE_FIELD[key];
            if (!profileKey) {
                // banking_details_parsed, traded_commodities, contacts_parsed: always pre-check
                initialSelected[key] = true;
                return;
            }
            initialSelected[key] = isProfileFieldEmpty(existingProfileData[profileKey]);
        });
        return initialSelected;
    }, [existingProfileData]);

    // Check if we have results from background processing
    useEffect(() => {
        if (documentScanningState?.result && documentScanningState.result !== extractedData) {
            console.log('📄 Received document scanning results from background');
            setExtractedData(documentScanningState.result);
            setUploadState('results');
            setSelectedFields(computeInitialSelectedFields(documentScanningState.result));
        }
    }, [documentScanningState, computeInitialSelectedFields, extractedData]);
    
    // Progress polling function
    const pollProgress = useCallback(async (analysisId) => {
        try {
            const response = await axiosInstance.get(`/api/crm/documents/progress/${analysisId}/`);
            const { current_page, total_pages, is_complete } = response.data;
            
            setPageProgress({ current: current_page || 0, total: total_pages || 0 });
            
            return is_complete;
        } catch (error) {
            console.error('Progress polling error:', error);
            return false;
        }
    }, []);

    // Start progress polling when we have an analysis ID during extraction
    useEffect(() => {
        let progressInterval;
        
        if (uploadState === 'extracting' && analysisId) {
            // Start progress polling every 500ms for responsive updates
            progressInterval = setInterval(async () => {
                const isComplete = await pollProgress(analysisId);
                if (isComplete) {
                    clearInterval(progressInterval);
                }
            }, 500);
        }
        
        return () => {
            if (progressInterval) clearInterval(progressInterval);
        };
    }, [uploadState, analysisId, pollProgress]);

    // Cycle through extraction steps during processing
    useEffect(() => {
        let interval;
        
        if (uploadState === 'extracting') {
            setExtractionStep(0);
            
            interval = setInterval(() => {
                const currentStepElement = document.querySelector('.current-step');
                if (currentStepElement) {
                    currentStepElement.style.opacity = '0';
                    
                    setTimeout(() => {
                        setExtractionStep(prev => {
                            if (prev < extractionSteps.length - 1) {
                                return prev + 1;
                            }
                            return 0;
                        });
                        
                        setTimeout(() => {
                            if (currentStepElement) {
                                currentStepElement.style.opacity = '1';
                            }
                        }, 100);
                    }, 500);
                }
            }, 3000);
        }
        
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [uploadState, extractionSteps.length]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setError('');
    };

    const acceptDroppedFile = (droppedFile) => {
        setFile(droppedFile);
        setError('');
    };

    const reportDropProblem = (dropError) => {
        setError(dropError);
        showError(dropError);
    };

    const validateFile = (file) => {
        if (!file) {
            return 'Please select a file';
        }
        
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxSize) {
            return 'File size must be less than 50MB';
        }
        
        return null;
    };

    // Poll the shared progress endpoint until the analysis completes; used by
    // both the upload flow and the KYC-documents-on-file flow.
    const startCompletionPolling = (analysisId) => {
        let pollAttempts = 0;
        const maxPollAttempts = 300; // 5 minutes at 1 second intervals

        const pollForCompletion = async () => {
            try {
                const progressResponse = await axiosInstance.get(`/api/crm/documents/progress/${analysisId}/`);
                const { is_complete, status, extracted_data, error_message } = progressResponse.data;

                console.log(`Poll attempt ${pollAttempts + 1}: Status=${status}, Complete=${is_complete}`);

                if (is_complete) {
                    console.log('Analysis completed!');

                    if (status === 'failed') {
                        throw new Error(error_message || 'Analysis failed');
                    }

                    // Analysis completed successfully
                    const validExtractedData = extracted_data || {};
                    setExtractedData(validExtractedData);
                    setUploadState('results');
                    setSelectedFields(computeInitialSelectedFields(validExtractedData));

                    return;
                }

                // Continue polling
                pollAttempts++;
                if (pollAttempts < maxPollAttempts) {
                    setTimeout(pollForCompletion, 1000);
                } else {
                    throw new Error('Analysis timeout - processing took too long');
                }

            } catch (error) {
                console.error('Polling error:', error);
                setError(getErrorMessage(error, kycFillMode
                    ? 'KYC documents could not be analyzed. Please try again.'
                    : 'Document could not be analyzed. Please try a different file.'));
                // In KYC mode there is no file-pick step to fall back to; the
                // 'error' state also keeps the auto-start effect from looping.
                setUploadState(kycFillMode ? 'error' : 'idle');
            }
        };

        // Start polling after 1 second
        setTimeout(pollForCompletion, 1000);
    };

    // KYC fill mode: start the on-file documents analysis as soon as the modal
    // opens (there is no file to pick). One shot per open — 'error' is a
    // terminal state here, not a retrigger.
    useEffect(() => {
        if (!isOpen || !kycFillMode || !kycPartnerId) return;
        if (uploadState !== 'idle' || analysisId || extractedData) return;

        const startKycAnalysis = async () => {
            setUploadState('extracting');
            setError('');
            try {
                const response = await axiosInstance.post(
                    `/api/crm/partners/${kycPartnerId}/analyze-kyc-documents/`);
                if (response.data.status === 'processing' && response.data.analysis_id) {
                    setAnalysisId(response.data.analysis_id);
                    if (onStartDocumentScanning) {
                        onStartDocumentScanning(response.data.analysis_id, 'KYC documents on file');
                    }
                    startCompletionPolling(response.data.analysis_id);
                }
            } catch (error) {
                console.error('KYC documents analysis error:', error);
                const errorMessage = getErrorMessage(error, 'KYC documents could not be analyzed. Please try again.');
                setError(errorMessage);
                showError(errorMessage);
                setUploadState('error');
            }
        };
        startKycAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, kycFillMode, kycPartnerId, uploadState, analysisId, extractedData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const fileError = validateFile(file);
        if (fileError) {
            setError(fileError);
            return;
        }
        
        setUploadState('extracting');
        setError('');
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            console.log('Starting CRM profile document analysis...');
            
            const response = await axiosInstance.post('/api/crm/documents/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('Immediate response received:', response.data);
            
            if (response.data.status === 'processing' && response.data.analysis_id) {
                console.log('Analysis started in background, analysis ID:', response.data.analysis_id);
                
                setAnalysisId(response.data.analysis_id);
                
                // Notify parent that document scanning is starting (for background processing tracking)
                if (onStartDocumentScanning) {
                    onStartDocumentScanning(response.data.analysis_id, file.name);
                }
                
                // Start polling for completion
                startCompletionPolling(response.data.analysis_id);
                return;
            }
            
        } catch (error) {
            console.error('CRM document analysis error:', error);
            
            const errorMessage = getErrorMessage(error, 'Document analysis failed. Please try a different file or try again later.');
            
            setError(errorMessage);
            showError(errorMessage);
            setUploadState('error');
        }
    };

    const handleClose = async () => {
        // If processing, allow closing and let it continue in background
        if (uploadState === 'extracting') {
            console.log('📄 Closing modal while document scanning continues in background');
            showInfo('Document scanning continues in background. You will be notified when complete.');
            onClose();
            return;
        }
        
        // If we have an analysis ID and are closing/canceling, discard the analysis
        if (analysisId && uploadState === 'results') {
            try {
                console.log('Discarding CRM document analysis on close:', analysisId);
                await axiosInstance.delete(`/api/crm/documents/discard/${analysisId}/`);
                console.log('CRM document analysis discarded on close');
            } catch (error) {
                console.error('Failed to discard analysis:', error);
            }
        }
        
        setFile(null);
        setError('');
        setUploadState('idle');
        setExtractedData(null);
        setSelectedFields({});
        setAnalysisId(null);
        setNamingPayload(null);
        onClose();
    };

    const handleFieldToggle = (fieldName) => {
        setSelectedFields(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const handleDiscard = async () => {
        if (analysisId) {
            try {
                console.log('Discarding CRM document analysis:', analysisId);
                showSuccess('Discarding document analysis...');
                
                await axiosInstance.delete(`/api/crm/documents/discard/${analysisId}/`);
                
                console.log('CRM document analysis discarded successfully');
                showSuccess('Document analysis discarded successfully');
            } catch (error) {
                console.error('Failed to discard analysis:', error);
                
                if (error.response?.status === 404) {
                    console.log('Analysis already deleted or not found');
                } else {
                    showError('Failed to discard analysis. Please try again.');
                    return;
                }
            }
        }
        
        handleClose();
    };

    const handleApply = () => {
        // Get selected data for profile pre-fill
        const selectedData = {};
        Object.keys(selectedFields).forEach(key => {
            if (selectedFields[key] && extractedData[key]) {
                selectedData[key] = extractedData[key];
            }
        });
        
        console.log('Applying selected profile data:', selectedData);
        // Carry the originating filename on the naming payload so the parent
        // can pass it to confirmName after the profile (and its DocuWare
        // document) is saved.
        const namingPayloadWithFile = namingPayload
            ? { ...namingPayload, originalFilename: file?.name || '' }
            : null;
        onSuccess({
            selectedData,
            analysisId,
            namingPayload: namingPayloadWithFile,
            // 'kyc' = filled from documents already on file: the parent must
            // NOT wire this analysis to the next save's DocuWare upload.
            // Checked on the DATA as well as the mode: a KYC analysis finished
            // in the background can surface in a later upload-mode open, and
            // must still never be treated as an uploadable document.
            source: (kycFillMode || extractedData?.kyc_fill_mode) ? 'kyc' : 'upload',
        });
        // Don't close or discard - parent will handle cleanup
    };

    if (!isOpen) return null;

    return (
        <div className="crm-document-modal-overlay">
            <div className="crm-document-modal-content">
                <div className="crm-document-modal-header">
                    <h2>
                        {kycFillMode
                            ? 'Fill Missing Details from KYC Documents'
                            : (uploadState === 'results'
                                ? (existingProfileData ? 'Fill Partner Data from Document' : 'Profile Data Extraction Results')
                                : (existingProfileData ? 'Upload Document to Fill Partner Info' : 'Upload Profile Document'))}
                    </h2>
                    <button 
                        className="crm-document-close-button"
                        onClick={handleClose}
                        disabled={uploadState === 'extracting'}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {error && (
                    <div className="crm-document-error-message">
                        <div className="crm-document-error-icon">
                            <FontAwesomeIcon icon={faTimes} />
                        </div>
                        <div className="crm-document-error-content">
                            <div className="crm-document-error-title">Document Processing Failed</div>
                            <div className="crm-document-error-text">
                                {error.split('\n').map((line, index) => (
                                    <div key={index} className="crm-document-error-line">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Upload State - File Selection (never shown in KYC fill mode:
                    the source documents are already on file) */}
                {uploadState === 'idle' && !kycFillMode && (
                    <form onSubmit={handleSubmit} className="crm-document-upload-form">
                        <FileDropZone
                            className={`crm-document-file-drop-zone ${file ? 'has-file' : ''}`}
                            activeClassName="active"
                            onFiles={(files) => acceptDroppedFile(files[0])}
                            onError={reportDropProblem}
                        >
                            {file ? (
                                <div className="crm-document-file-selected">
                                    <FontAwesomeIcon icon={faFileText} className="crm-document-file-icon" />
                                    <div className="crm-document-file-info">
                                        <span className="crm-document-file-name">{file.name}</span>
                                        <span className="crm-document-file-size">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="crm-document-remove-file"
                                        onClick={() => setFile(null)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            ) : (
                                <div className="crm-document-drop-zone-content">
                                    <FontAwesomeIcon icon={faUpload} className="crm-document-upload-icon" />
                                    <p className="crm-document-drop-text">
                                        Drag and drop your profile document here, or{' '}
                                        <label className="crm-document-file-select-link">
                                            browse files
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rtf,.txt,.md,.html,.csv,.png,.jpg,.jpeg,.tiff,.bmp,.webp,.heic,.eml,.msg,.zip,.json,.yaml,.yml,.xhtml"
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </p>
                                    <p className="crm-document-file-requirements">
                                        Supported formats: PDF, Word, Excel, PowerPoint, images (including iPhone photos), text files, email, archives (max 50MB)
                                    </p>
                                </div>
                            )}
                        </FileDropZone>
                        
                        <div className="crm-document-upload-info">
                            <h3>AI-Powered Profile Extraction</h3>
                            <ul>
                                <li>AI will extract legal entity name, address, website, and more</li>
                                <li>Banking details and traded commodities will be parsed</li>
                                <li>You can review and select which fields to use</li>
                                <li>Profile will be created with extracted data after you save</li>
                            </ul>
                        </div>
                        
                        <div className="crm-document-modal-actions centered">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!file}
                            >
                                <FontAwesomeIcon icon={faRobot} />
                                Analyze Document
                            </button>
                        </div>
                    </form>
                )}

                {/* Loading State (in KYC fill mode 'idle' is a brief pre-start
                    flash before the auto-start effect fires — show loading) */}
                {(uploadState === 'extracting' || (kycFillMode && uploadState === 'idle')) && (
                    <div className="crm-document-loading-state">
                        <div className="crm-document-loading-content">
                            <div className="extraction-progress" style={{
                                textAlign: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <p className="current-step" style={{
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    color: '#374151',
                                    margin: '0 0 0.75rem 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    opacity: 1,
                                    transition: 'opacity 0.5s ease-in-out',
                                    minHeight: '1.5rem'
                                }}>
                                    <FontAwesomeIcon 
                                        icon={faRobot} 
                                        className="ai-icon" 
                                        style={{ color: '#3b82f6', fontSize: '1.1rem' }}
                                    />
                                    {extractionSteps[extractionStep]}
                                </p>
                                
                                {/* Page Progress Display */}
                                {pageProgress.total > 0 && (
                                    <div className="page-progress" style={{
                                        fontSize: '0.9rem',
                                        color: '#6b7280',
                                        fontWeight: '500',
                                        marginTop: '0.5rem'
                                    }}>
                                        Extracting data from page {pageProgress.current}/{pageProgress.total}
                                    </div>
                                )}
                            </div>
                            
                            <div className="timing-info" style={{
                                marginTop: '1.5rem',
                                textAlign: 'center'
                            }}>
                                <div className="timing-details" style={{
                                    marginBottom: '1rem'
                                }}>
                                    <div className="standard-timing" style={{
                                        padding: '0.5rem',
                                        backgroundColor: '#f0f9ff',
                                        border: '1px solid #3b82f6',
                                        borderRadius: '0.375rem',
                                        color: '#1e40af'
                                    }}>
                                        <small style={{ fontWeight: '500' }}>
                                            Typical processing time: 30-60 seconds
                                        </small>
                                    </div>
                                </div>
                                
                                <div className="extraction-details" style={{
                                    padding: '0.75rem',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0.375rem',
                                    color: '#64748b',
                                    fontSize: '0.8rem',
                                    lineHeight: '1.4'
                                }}>
                                    <small>
                                        Our AI analyzes document structure, applies OCR for scanned content, 
                                        and extracts key partner profile details including company information, 
                                        banking details, and trading commodities.
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Results State */}
                {uploadState === 'results' && extractedData && (
                    <CRMExtractionResults
                        extractedData={extractedData}
                        selectedFields={selectedFields}
                        onFieldToggle={handleFieldToggle}
                        onApply={handleApply}
                        onDiscard={handleDiscard}
                        existingProfileData={existingProfileData}
                    >
                        {kycFillMode ? (
                            /* Source accounting: which KYC documents were read,
                               and — just as important — which could NOT be, so a
                               partial result never masquerades as full coverage. */
                            Array.isArray(extractedData.source_documents) && extractedData.source_documents.length > 0 && (
                                <div className="crm-document-naming-section" style={{ fontSize: '0.85rem' }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0' }}>KYC documents used</h4>
                                    <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                        {extractedData.source_documents.map((doc, index) => (
                                            <li
                                                key={index}
                                                style={{ color: doc.status === 'used' ? '#166534' : '#b45309' }}
                                            >
                                                {doc.name} — {doc.status === 'used' ? 'read' : `${doc.status}: ${doc.detail}`}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )
                        ) : (
                            /* AI Document Name — shown AFTER the extracted info so
                                the user reviews/edits the suggested name. Non-blocking:
                                Apply still works if no suggestion is produced. */
                            <div className="crm-document-naming-section">
                                <DocumentNameSuggestionForm
                                    autoRun
                                    file={file}
                                    module="crm_profile_doc"
                                    partnerName={extractedData?.legal_entity_name || ''}
                                    onChange={setNamingPayload}
                                />
                            </div>
                        )}
                    </CRMExtractionResults>
                )}
            </div>
        </div>
    );
};

export default CRMDocumentUploadModal;

